import React, { useState, useEffect } from 'react';
import { Icons, getAppIcon } from '../../../components/ui/Icons.tsx';
import { ConnectedApp, Project } from '../../../types';
import ChatInput from './ChatInput';

// ---------------------------------------------------------------------------
// AppHeader
// ---------------------------------------------------------------------------

interface AppHeaderProps {
  isSidebarExpanded: boolean;
  onToggleSidebar: () => void;
  title?: string;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ isSidebarExpanded, onToggleSidebar, title = 'Neura' }) => (
  <header className="fixed top-0 right-0 left-0 h-16 md:h-20 bg-white/95 dark:bg-[#050505]/95 backdrop-blur-sm flex items-center justify-between px-4 md:px-6 z-40 md:ml-16 pointer-events-none transition-all duration-350">
    <div className="flex items-center gap-3 pointer-events-auto">
      <button
        onClick={onToggleSidebar}
        className="md:hidden p-2 -ml-2 text-gray-900 dark:text-white hover:bg-gray-105 dark:hover:bg-[#212121] rounded-xl pointer-events-auto"
      >
        <Icons.Menu className="w-6 h-6" />
      </button>
      <div
        className={`flex items-center gap-3 transition-opacity duration-200 
          ${isSidebarExpanded ? 'opacity-0' : 'opacity-100'} 
          ${isSidebarExpanded ? 'hidden md:flex' : 'flex'}
        `}
        aria-hidden={isSidebarExpanded}
      >
        <span className="text-xl font-bold text-gray-900 dark:text-white tracking-tight font-sans">{title}</span>
      </div>
    </div>
    <div className="flex items-center gap-4" />
  </header>
);

// ---------------------------------------------------------------------------
// ConnectedApps
// ---------------------------------------------------------------------------

interface ConnectedAppsProps {
  apps: ConnectedApp[];
  onToggleApp: (id: string) => void;
  onOpenIntegrations: () => void;
}

export const ConnectedApps: React.FC<ConnectedAppsProps> = ({ apps, onToggleApp, onOpenIntegrations }) => (
  <div className="flex flex-wrap justify-center gap-2.5 mt-6 max-w-2xl">
    {apps.map((app) => {
      const Icon = getAppIcon(app.id);
      return (
        <button
          key={app.id}
          onClick={() => onToggleApp(app.id)}
          className={`
            group flex items-center gap-2.5 px-3.5 py-2 rounded-xl border transition-all duration-200 font-sans
            ${app.status === 'connected'
              ? 'bg-white dark:bg-[#121212] border-gray-200 dark:border-white/10 shadow-sm hover:border-gray-300 dark:hover:border-white/20'
              : 'bg-transparent dark:bg-[#050505] border-gray-150 dark:border-white/5 text-gray-400 hover:border-gray-250 dark:hover:border-white/10 hover:bg-gray-50 dark:hover:bg-[#121212]/30'
            }
          `}
        >
          <div className={`w-5 h-5 shrink-0 ${app.status === 'connected' ? 'opacity-100' : 'opacity-50 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all'}`}>
            <Icon className="w-full h-full object-contain" />
          </div>
          <span className={`text-xs font-semibold transition-colors duration-200 ${app.status === 'connected' ? 'text-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-white'}`}>
            {app.name}
          </span>
          <div className="ml-1">
            {app.status === 'connected' ? (
              <div className="w-1.5 h-1.5 rounded-full bg-black dark:bg-white" />
            ) : (
              <Icons.Plus className="w-3 h-3 text-gray-350 dark:text-gray-605 group-hover:text-gray-500 dark:group-hover:text-gray-400" />
            )}
          </div>
        </button>
      );
    })}

    <button
      onClick={onOpenIntegrations}
      className="group flex items-center gap-2.5 px-3.5 py-2 rounded-xl border border-dashed border-gray-200 dark:border-white/5 text-gray-450 dark:text-gray-400 hover:border-gray-300 dark:hover:border-white/15 hover:text-gray-700 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#121212]/30 transition-all duration-200 font-sans"
    >
      <div className="w-5 h-5 shrink-0 flex items-center justify-center">
        <Icons.Apps className="w-4 h-4" />
      </div>
      <span className="text-xs font-semibold">More</span>
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// PromptSuggestions
// ---------------------------------------------------------------------------

interface PromptSuggestionsProps {
  suggestions: string[];
  onSelect: (text: string) => void;
}

export const PromptSuggestions: React.FC<PromptSuggestionsProps> = ({ suggestions, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 w-full max-w-3xl mt-6">
    {suggestions.map((text, index) => (
      <button
        key={index}
        onClick={() => onSelect(text)}
        className="
          flex items-center justify-center p-3.5 rounded-xl
          bg-gray-50 dark:bg-[#121212] border border-gray-200 dark:border-gray-800
          text-xs text-gray-655 dark:text-gray-300 font-semibold text-center
          hover:bg-white dark:hover:bg-[#050505] hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm hover:text-black dark:hover:text-white
          transition-all duration-200 select-none
          active:scale-[0.98] font-sans
        "
      >
        "{text}"
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// WelcomeView
// ---------------------------------------------------------------------------

interface WelcomeViewProps {
  inputValue: string;
  onInputChange: (val: string) => void;
  onSend: (msg: string, file?: File) => void;
  userName: string;
  onOpenIntegrations: () => void;
  onOpenSchedules?: (prefill?: string) => void;
  isScheduleMode?: boolean;
  onToggleScheduleMode?: () => void;
  apps: ConnectedApp[];
  onToggleApp: (id: string) => void;
  projects: Project[];
  onSelectProject: (project: Project) => void;
}

const WelcomeView: React.FC<WelcomeViewProps> = ({
  inputValue,
  onInputChange,
  onSend,
  userName,
  onOpenIntegrations,
  onOpenSchedules,
  isScheduleMode,
  onToggleScheduleMode,
  apps,
  onToggleApp,
  projects = [],
  onSelectProject,
}) => {
  const [greeting, setGreeting] = useState('Good morning');

  const displayApps = (apps || []).slice(0, 5);

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 12) setGreeting('Good morning');
      else if (hour >= 12 && hour < 17) setGreeting('Good afternoon');
      else setGreeting('Good evening');
    };
    updateGreeting();
    const interval = setInterval(updateGreeting, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-4 md:px-6 animate-in fade-in duration-700 overflow-y-auto pb-20 no-scrollbar">
      <h1 className="text-4xl md:text-6xl font-serif text-gray-900 dark:text-white mb-8 md:mb-12 text-center tracking-tight leading-tight">
        {greeting}, {userName}
      </h1>
      <div className="w-full flex justify-center mb-6 md:mb-8">
        <ChatInput
          value={inputValue}
          onChange={onInputChange}
          onSend={onSend}
          onOpenIntegrations={onOpenIntegrations}
          onOpenSchedules={onOpenSchedules}
          isScheduleMode={isScheduleMode}
          onToggleScheduleMode={onToggleScheduleMode}
        />
      </div>
      <ConnectedApps
        apps={displayApps}
        onToggleApp={onToggleApp}
        onOpenIntegrations={onOpenIntegrations}
      />
    </div>
  );
};

export default WelcomeView;
